/**
 * Pure helpers for the per-line review-tier answer flow (CSR review-noise
 * reduction). The backend tiers every needs_review flag into BLOCKER /
 * VERIFY / INFO (services/orders/review_tiering.py); these helpers decide
 * what the portal renders from those fields.
 *
 * - lineNeedsAnswer: the line demands a CSR answer before submit
 * - isInfoTier: corroborated-safe noise, auto-checked, never demands attention
 * - attentionSummary: counts for chips/badges (orders list, answer panel)
 * - describeEvidence: short human phrase for the tier rule that fired
 */

import type { OrderLineItem, ReviewEvidence } from '../types/orders';

/** Tier rules that mean "the line has no usable count until a person types one". */
const QUANTITY_ENTRY_RULES: ReadonlySet<string> = new Set([
  'nonpositive_quantity',
  'quantity_unreadable',
]);

/** The line-item fields the quantity-entry helper reads. */
export type QuantityEntryFields = Pick<
  OrderLineItem,
  'needs_review' | 'review_evidence' | 'quantity'
>;

/**
 * True when the only honest answer to this line's card is a typed count:
 * the flag's rule says the quantity is missing or could not be read from
 * the PO, and the row still has no positive quantity. Sage cannot take the
 * line without one, so "send as-is" is not an option (kanban f9350c24,
 * AU02520). Once a count is on the row (typed on the card or on the row
 * itself) the ordinary accept flow applies again.
 */
export function needsQuantityEntry(li: QuantityEntryFields): boolean {
  if (!li.needs_review) return false;
  const rule = li.review_evidence?.rule;
  if (typeof rule !== 'string' || !QUANTITY_ENTRY_RULES.has(rule)) return false;
  const qty = li.quantity == null ? NaN : parseFloat(String(li.quantity));
  return !(qty > 0);
}

/** The subset of line-item fields the review helpers read. */
export type ReviewLineFields = Pick<
  OrderLineItem,
  'needs_review' | 'review_tier' | 'review_resolution'
>;

/**
 * True when this line still demands a CSR answer: flagged, not
 * corroborated-safe (INFO), and not yet resolved via the answer panel.
 */
export function lineNeedsAnswer(li: ReviewLineFields): boolean {
  return !!li.needs_review && li.review_tier !== 'INFO' && !li.review_resolution;
}

/** True when this line's flag is corroborated-safe noise (auto-checked). */
export function isInfoTier(li: ReviewLineFields): boolean {
  return !!li.needs_review && li.review_tier === 'INFO';
}

/**
 * True when the CSR already answered this line's review card (the PATCH
 * cleared needs_review and stamped a resolution). Undo-able until submit.
 */
export function isResolvedReviewLine(li: ReviewLineFields): boolean {
  return !li.needs_review && li.review_resolution != null;
}

/** A single distinct question inside a line's answer card. */
export interface QuestionPart {
  text: string;
  tier: string;
}

/** The line-item fields the question-part helper reads. */
export type QuestionPartFields = Pick<
  OrderLineItem,
  'review_reason_parts' | 'needs_review_reason' | 'review_tier'
>;

/**
 * The distinct questions the answer card steps through for a line: the
 * serve-time reason fragments when the API provides them, else the whole
 * reason as a single step. Empty when the line has no reason text.
 */
export function questionPartsFor(li: QuestionPartFields): QuestionPart[] {
  if (li.review_reason_parts && li.review_reason_parts.length > 0) {
    return li.review_reason_parts;
  }
  if (li.needs_review_reason) {
    return [{ text: li.needs_review_reason, tier: li.review_tier ?? 'VERIFY' }];
  }
  return [];
}

/**
 * Label for the amber expander chip on a flagged line row, e.g.
 * "2 issues" / "1 issue" / "blocked · 2 issues". The chip toggles the
 * inline answer card under the row.
 */
export function issueChipLabel(
  parts: readonly QuestionPart[],
  isBlocker: boolean,
): string {
  const n = Math.max(parts.length, 1);
  const noun = `${n} issue${n === 1 ? '' : 's'}`;
  return isBlocker ? `blocked · ${noun}` : noun;
}

export interface AttentionSummary {
  /** Unresolved BLOCKER/VERIFY lines — each needs a CSR answer. */
  toAnswer: number;
  /** INFO-tier lines — auto-checked, listed in the muted drawer only. */
  infoCount: number;
  /** Subset of toAnswer at BLOCKER tier (cannot be sent as-is). */
  blockers: number;
}

/** Summarize review attention across an order's line items. */
export function attentionSummary(
  lineItems: readonly ReviewLineFields[],
): AttentionSummary {
  let toAnswer = 0;
  let infoCount = 0;
  let blockers = 0;
  for (const li of lineItems) {
    if (lineNeedsAnswer(li)) {
      toAnswer += 1;
      if (li.review_tier === 'BLOCKER') blockers += 1;
    } else if (isInfoTier(li)) {
      infoCount += 1;
    }
  }
  return { toAnswer, infoCount, blockers };
}

/**
 * Short human phrases for the tier rules emitted by
 * services/orders/review_tiering.py. Unknown rules (e.g. legacy
 * blocker_reason_text / unclassified_reason_text) fall back to a generic
 * phrase rather than being hidden.
 */
const RULE_PHRASES: Record<string, string> = {
  missing_item_code: 'No product code could be resolved',
  catalog_missing_item_code: 'Item code is not in the Sage catalog',
  inactive_item_code: 'Item code is inactive/discontinued in Sage',
  dead_catalog_item:
    'Appears discontinued in Sage (no price, no stock, not sold in 2+ years): confirm availability',
  nonpositive_quantity: 'Quantity is missing or not positive',
  quantity_unreadable:
    'The quantity could not be read from the PO: enter the count the customer wants',
  unit_synonym: "Customer's unit matches the catalog unit (synonym)",
  each_count_single_container: 'Each-count on a single-container product',
  uom_counting_basis: "Customer's unit changes the counting basis",
  uom_unknown_catalog: 'Catalog unit of measure is unknown',
  agent_hedge: 'The AI was not fully confident about this line',
  orderability_sheet_note: 'Note from the orderability sheet',
  expired_special_price_conflict:
    'Expired special price conflicts with the PO price',
  unclassified_flag: 'Flagged for review',
};

const FALLBACK_PHRASE = 'Flagged for review';

/**
 * Human phrase for the review-evidence rule. Returns null when there is no
 * evidence or no rule (nothing to describe).
 */
export function describeEvidence(
  evidence: ReviewEvidence | null | undefined,
): string | null {
  const rule = evidence?.rule;
  if (typeof rule !== 'string' || rule.length === 0) return null;
  return RULE_PHRASES[rule] ?? FALLBACK_PHRASE;
}
